import { Subscription } from '../../hooks/subscriptionApi';
import { serviceHelper } from '../../../../../config/service-helper';
import { LinkedId } from '../customer/types';
import { SubscriptionApiRecord, SubscriptionApiResponse, SubscriptionTypes } from './types';
import { UnifiedApiClient } from '../UnifiedApiClient';
import { handleShopifyLogout } from '../../../../../utils/signInOutUp/AuthenticationHelper';
const { unifiedSubscriptionApi: subscriptionApi } = serviceHelper;

export class SubscriptionsClient extends UnifiedApiClient {
  protected static _refreshUrl = serviceHelper.customerAuthRefresh;
  protected static _logout = handleShopifyLogout;

  static getSubscription = async ({
    subscriptionType,
    customerId,
    signal,
  }: {
    subscriptionType: string;
    customerId: string;
    signal?: AbortSignal;
  }): Promise<SubscriptionApiResponse> => {
    const { data, statusCode, error } = await this._fetch<Record<string, any>>({
      path: `${subscriptionApi}/GetSubscription`,
      method: 'POST',
      body: { type: subscriptionType, customerId },
      signal,
    });

    return {
      data: this.mapFromRecord({ ...(data ?? {}), type: subscriptionType } as SubscriptionApiRecord),
      statusCode,
      error,
    };
  };

  static createSubscription = async ({
    subscriptionType,
    customerId,
    linkedId,
    parentId,
    sourceLocation,
    storeId,
  }: {
    subscriptionType: string;
    customerId: string;
    linkedId: LinkedId;
    parentId?: string;
    sourceLocation?: string;
    storeId?: string;
  }): Promise<SubscriptionApiResponse> => {
    const { data, statusCode, error } = await this._fetch<Record<string, any>>({
      path: `${subscriptionApi}/PostCreateSubscription`,
      method: 'POST',
      body: {
        customerId,
        type: subscriptionType,
        ...linkedId,
        parentId,
        sourceLocation: sourceLocation ?? 'Online',
        storeId,
        isActive: true,
        isRecurring: false,
      },
    });
    return {
      data: this.mapFromRecord({ ...data, type: subscriptionType } as SubscriptionApiRecord),
      statusCode,
      error,
    };
  };

  static updateSubscription = async ({
    customerId,
    subscriptionId,
    updateFields,
  }: {
    customerId: string;
    subscriptionId: string;
    updateFields: Subscription;
  }): Promise<SubscriptionApiResponse> => {
    const { data, statusCode, error } = await this._fetch({
      path: `${subscriptionApi}/PostModifySubscription`,
      method: 'POST',
      body: {
        customerId,
        id: subscriptionId,
        ...updateFields,
      },
    });
    return { data: this.mapFromRecord(data as SubscriptionApiRecord), statusCode, error };
  };

  static cancelSubscription = async ({
    customerId,
    type,
  }: {
    customerId: string;
    type: SubscriptionTypes | string | undefined;
  }): Promise<SubscriptionApiResponse> => {
    const { data, statusCode, error } = await this._fetch({
      path: `${subscriptionApi}/PostCancelSubscription`,
      method: 'POST',
      body: {
        customerId,
        type,
      },
    });
    return { data: this.mapFromRecord(data as SubscriptionApiRecord), statusCode, error };
  };

  static mapFromRecord = (record: SubscriptionApiRecord): Subscription => {
    return record?.id
      ? {
          id: record.id,
          type: record.type,
          isActive: record.isActive,
          startDate: (record.startDate && new Date(record.startDate)) || undefined,
          endDate: (record.endDate && new Date(record.endDate)) || undefined,
          parentId: record.parentId,
          coupons: record.coupons,
        }
      : {
          type: record?.type ?? '',
          isActive: false,
        };
  };
}
